import { Box, Button, DialogActions, FormControlLabel, Radio, RadioGroup, TextField, Typography } from "@material-ui/core"
import { Dialog, DialogContent, DialogTitle } from "@material-ui/core"
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import FormAutocomplete from "../form/FormAutocomplete";
import FormTextField from "../form/FormTextField";
import FormDatePicker from "../form/FormDatePicker";
import moment from "moment";

// Returns the invoicing forecast lines linked to this invoice (via invoice_detail -> invoicing_forecast_details)
const getLinkedForecasts = (invoice, invoicingForecasts) => {
    const detailIds = (invoice?.invoice_details || []).map(d => d.id);
    if(!detailIds.length || !invoicingForecasts?.length) return [];
    return invoicingForecasts.filter(forecast =>
        (forecast.invoicing_forecast_details || []).some(fd => detailIds.includes(fd.invoice_detail_id))
    );
}

const DialogEmitInvoice = ({open,setOpen,template,invoices,folder,companies,generatePdf,emitInvoice,fetchSubfoldersInvoices,uploadInvoicesFile,subfolder,invoiceEmittedId,invoicingForecasts,editInvoicingForecast}) => {

    const [documentMethod,setDocumentMethod] = useState(null);
    const [isFileUploaded,setIsFileUploaded] = useState(false);
    const [mismatchOpen,setMismatchOpen] = useState(false);
    const [mismatchedForecasts,setMismatchedForecasts] = useState([]);
    const [pendingEmit,setPendingEmit] = useState(null);
    const file = useRef(null)
    // tracks whether the user has manually overridden the auto-computed term date
    // (e.g. a contract term different from the default 30 days) so it isn't
    // silently overwritten if the issue date is changed afterwards
    const termDateManuallyEdited = useRef(false);

    useEffect(()=>{
        setIsFileUploaded(false)
    },[documentMethod])

    const form = useForm({
        defaultValues:{
            companyId:folder ? folder.end_user_company_id : null,
            invoice_type:'invoice_fr',
            number: invoices?.[0]?.number || null,
            memo: invoices?.[0]?.memo || '',
            comment: invoices?.[0]?.comment || '',
            issue_date: invoices?.[0]?.issue_date || null,
            term_date: invoices?.[0]?.term_date || (invoices?.[0]?.issue_date ? moment(invoices[0].issue_date).add(30,'days') : null),
        }
    })

    useEffect(()=>{
        termDateManuallyEdited.current = false;
        form.setValue('number', invoices?.[0]?.number || null)
        form.setValue('memo', invoices?.[0]?.memo || '')
        form.setValue('comment', invoices?.[0]?.comment || '')
        form.setValue('issue_date', invoices?.[0]?.issue_date || null)
        form.setValue('term_date', invoices?.[0]?.term_date || (invoices?.[0]?.issue_date ? moment(invoices[0].issue_date).add(30,'days') : null))
        // eslint-disable-next-line
    },[invoices])

    const handleIssueDateChange = (date) => {
        // keep the default (issue date + 30 days) in sync only until the user
        // has explicitly set a different term date themselves
        if(!termDateManuallyEdited.current && date){
            form.setValue('term_date', moment(date).add(30,'days'))
        }
    }

    const handleTermDateChange = () => {
        termDateManuallyEdited.current = true;
    }

    const proceedToEmit = (withoutDoc) => {
        if(withoutDoc){
            emitInvoice(form,invoices)
            setOpen(false);
        }
        else{
            const payload = {
                file:file.current.files[0],
                invoice_id:invoices[0].id
            }
            uploadInvoicesFile(payload).then((res)=>{
                //fetchSubfolder(subfolderId);
                fetchSubfoldersInvoices({subfolder_id:subfolder.id,folder_id:folder.id}).then((res)=>{
                    if(!invoiceEmittedId)emitInvoice(form,invoices)
                    setOpen(false);
                })
            });
        }
    }

    const handleEmit = (withoutDoc) => {
        const number = form.getValues('number');
        if(!number || !number.toString().trim()){
            form.setError('number', { type: 'required', message: 'Invoice number is required to emit an invoice' });
            return;
        }
        const issue_date = form.getValues('issue_date');
        if(!issue_date){
            form.setError('issue_date', { type: 'required', message: 'Issue date is required to emit an invoice' });
            return;
        }
        const term_date = form.getValues('term_date');
        if(!term_date){
            form.setError('term_date', { type: 'required', message: 'Term date is required to emit an invoice' });
            return;
        }

        // consistency check: issue_date must match the forecast_date of the linked forecast line(s)
        const linkedForecasts = getLinkedForecasts(invoices?.[0], invoicingForecasts);
        const mismatches = linkedForecasts.filter(forecast =>
            moment(forecast.forecast_date).format('YYYY-MM-DD') !== moment(issue_date).format('YYYY-MM-DD')
        );

        if(mismatches.length > 0){
            setMismatchedForecasts(mismatches);
            setPendingEmit(withoutDoc);
            setMismatchOpen(true);
            return;
        }

        proceedToEmit(withoutDoc);
    }

    const handleAlignForecastDates = () => {
        const issue_date = form.getValues('issue_date');
        Promise.all(mismatchedForecasts.map(forecast => editInvoicingForecast({
            id: forecast.id,
            invoicingForecast: { forecast_date: issue_date }
        }))).then(() => {
            proceedToEmit(pendingEmit);
            setMismatchOpen(false);
            setPendingEmit(null);
        });
    }

    const handleKeepEditing = () => {
        setMismatchOpen(false);
        setPendingEmit(null);
    }

    return (<div>
        <Dialog
            open={open}
            onClose={()=>setOpen(false)}
            aria-labelledby="form-dialog-title"
            maxWidth="sm"
            fullWidth="true"
        >
            <DialogTitle id="form-dialog-title">Emit invoice</DialogTitle>
            <DialogContent>
                <Box marginBottom="20px">
                <FormProvider {...form}>
                    {!invoiceEmittedId && <>
                    <Typography variant="h6">Invoice details</Typography>
                    <Box display="flex" flexDirection="row" marginBottom="10px">
                        <Box display="flex" flex={0.5} marginLeft="10px">
                            <FormTextField name="number" label="invoice number" tableField/>
                        </Box>
                    </Box>
                    <Box display="flex" flexDirection="row" marginBottom="10px">
                        <Box display="flex" flex={0.5} marginLeft="10px">
                            <FormDatePicker label="Issue date" name="issue_date" onChangeEvent={handleIssueDateChange}/>
                        </Box>
                        <Box display="flex" flex={0.5} marginLeft="10px">
                            <FormDatePicker label="Term date" name="term_date" onChangeEvent={handleTermDateChange}/>
                        </Box>
                    </Box>
                    <Box display="flex" flexDirection="row" marginBottom="10px">
                        <Box display="flex" flex={0.5} marginLeft="10px">
                            <FormTextField name="memo" label="Description (optional)" tableField/>
                        </Box>
                        <Box display="flex" flex={0.5} marginLeft="10px">
                            <FormTextField name="comment" label="Commentaire interne (optional)" tableField/>
                        </Box>
                    </Box>
                    <Typography variant="h6">Company</Typography>
                    <Box display="flex" flexDirection="row" marginBottom="10px">
                        <Box display="flex" flex={0.5} marginLeft="10px">
                        {invoices[0]?.invoice_type === "production" &&
                            <FormAutocomplete
                                style={{maxWidth:400,minWidth:400,display:"flex"}}
                                name="companyId"
                                label="Choisir un Client"
                                options={companies}
                                mapOptionToValue={company => company.id}
                                mapOptionToText={company => company.name}
                                tableField
                            />
                        }
                        </Box>
                    </Box></>}
                    <Typography variant="h6">Invoice document</Typography>
                    <Box marginLeft="10px">
                        <RadioGroup onChange={(e)=>setDocumentMethod(e.target.value)}>
                            <Box display="flex" flexDirection="row">
                                <FormControlLabel value="upload" control={<Radio />} label="Upload document" />
                                <FormControlLabel value="generate" control={<Radio />} label="Generate pdf" />
                            </Box>
                        </RadioGroup>
                    </Box>
                    {documentMethod && documentMethod === "generate" &&
                        (<Box display="flex" flexDirection="row">
                            <Box display="flex" flex={0.6} marginLeft="10px">
                                    {invoices[0]?.invoice_type === "production" &&
                                    <FormAutocomplete
                                        style={{maxWidth:400,minWidth:400,display:"flex"}}
                                        name="invoice_type"
                                        label="template"
                                        options={template.filter(temp=>{
                                            let str = temp.template_type.split('_');
                                            if(str[0] && str[0] === "invoice")return true
                                            return false
                                        })}
                                        mapOptionToValue={templ => templ.template_type}
                                        mapOptionToText={templ => templ.template_type}
                                    />}
                            </Box>
                            <Box flex={0.4} display="flex">
                                <Button onClick={()=>generatePdf(form,invoices)} variant="outlined" style={{width:"100%",marginTop:"auto",marginLeft:"10px"}}>Generate PDF</Button>
                            </Box>
                        </Box>)
                    }
                    {documentMethod && documentMethod === "upload" &&
                        (<Box display="flex" flexDirection="row">
                            <Box display="flex" flex={0.6} marginLeft="10px">
                                <TextField onChange={()=>setIsFileUploaded(true)} inputRef={file} type="file" style={{marginTop:"40px",width:"100%"}}/>
                            </Box>
                            <Box display="flex" flex={0.4} marginLeft="10px">
                                {isFileUploaded && <Button onClick={()=>handleEmit()} variant="outlined" style={{width:"100%",marginTop:"auto",marginLeft:"10px"}}>Emit invoice</Button>}
                            </Box>
                        </Box>)
                    }
                </FormProvider>
                </Box>
            </DialogContent>
            <DialogActions>
                     {!isFileUploaded && !invoiceEmittedId && <Button onClick={()=>handleEmit(true)} color="primary">Emit without document</Button>}
                    {invoiceEmittedId && <Button onClick={()=>setOpen(false)} color="primary">Close</Button>}
            </DialogActions>
        </Dialog>

        <Dialog open={mismatchOpen} onClose={handleKeepEditing} maxWidth="sm" fullWidth>
            <DialogTitle>Invoice date does not match the forecast</DialogTitle>
            <DialogContent>
                <Typography gutterBottom>
                    {`The invoice date you entered (${moment(form.getValues('issue_date')).format('DD/MM/YYYY')}) does not match the invoicing forecast date${mismatchedForecasts.length > 1 ? 's' : ''} below:`}
                </Typography>
                <Box marginTop={2} marginBottom={2}>
                    {mismatchedForecasts.map(forecast => (
                        <Typography key={forecast.id}>
                            - {forecast.memo || 'Forecast line'} : {moment(forecast.forecast_date).format('DD/MM/YYYY')}
                        </Typography>
                    ))}
                </Box>
                <Typography>
                    Do you want to update the forecast date(s) to match the invoice date, or go back and correct the invoice date instead?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleKeepEditing} color="primary">
                    Go back and fix invoice date
                </Button>
                <Button onClick={handleAlignForecastDates} color="primary" variant="contained">
                    Update forecast date(s) to match
                </Button>
            </DialogActions>
        </Dialog>
    </div>)
}

export default DialogEmitInvoice
